import React, { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import "../../index.css";
import "../../App.css";
import {
  Row,
  Col,
  Input,
  Typography,
  Radio,
  Button,
  Space,
  Select,
  DatePicker,
  Spin,
} from "antd";
import { RootState, useAppDispatch } from "../../redux-toolkit/store/store";
import { useSelector } from "react-redux";
import {
  handleGoToNextStep,
  handleUpdateErrorsOfOrderCreamForm,
  handleUpdateInjuryHistory,
  handleUpdateInjuryHistoryOpenWound,
  handleUpdateIfHasInjury,
  handleGoBack,
  saveStep6Api,
  getOrderByTransactionIdApi,
} from "../../redux-toolkit/slices/orderCreamSlice";
import { validateOrderCreamInjuryHistory } from "../../schemas/OrderCream";
import ErrorList from "../Items/ErrorsList";
import dayjs from "dayjs";

const { Option } = Select;

const OcInjuryhistory = () => {
  const dispatch = useAppDispatch();
  const orderCreamForm = useSelector((state: RootState) => state.orderCream);
  const {
    isError,
    errors,
    injuryHistory,
    transactionId,
    currentStep,
    isLoading,
  } = orderCreamForm;
  const [searchParams, setSearchParams] = useSearchParams();
  const [isLoadingOrder, setIsLoadingOrder] = useState(false);
  const { hasOpenWound, injuryEntries, injuryEntryChange } = injuryHistory;
  // const [hasPreviousDiag, setHasPreviousDiag] = useState<string>(""); // State for surgery/injury history question
  const [entries, setEntries] = useState([
    { id: Date.now(), type: "surgery", name: "", date: "", details: "" },
  ]);

  const handleOpenWoundChange = (e: any) => {
    dispatch(handleUpdateInjuryHistoryOpenWound(e.target.value));
  };

  const handlePreviousdiagChange = (e: any) => {
    dispatch(handleUpdateIfHasInjury(e.target.value));
    dispatch(
      handleUpdateErrorsOfOrderCreamForm({ isError: false, errors: [] })
    );
    // If selecting "yes" and entries is empty, initialize with one entry
    if (e.target.value === "yes" && entries.length === 0) {
      setEntries([
        { id: Date.now(), type: "surgery", name: "", date: "", details: "" },
      ]);
    }
  };

  const handleAddEntry = () => {
    setEntries([
      ...entries,
      { id: Date.now(), type: "surgery", name: "", date: "", details: "" },
    ]);
  };

  const handleRemoveEntry = (id: number) => {
    setEntries(entries.filter((entry) => entry.id !== id));
  };

  const handleEntryChange = (id: number, field: string, value: string) => {
    setEntries(
      entries.map((entry) =>
        entry.id === id ? { ...entry, [field]: value } : entry
      )
    );
  };
  // Fetch order data when component mounts
  useEffect(() => {
    const fetchOrder = async () => {
      if (transactionId) {
        setIsLoadingOrder(true);
        try {
          const orderData = await dispatch(
            getOrderByTransactionIdApi(transactionId)
          ).unwrap();
          // The Redux reducer will automatically populate the state with order data
        } catch (error) {
          console.error("Error loading order:", error);
        } finally {
          setIsLoadingOrder(false);
        }
      }
    };

    fetchOrder();
  }, [transactionId, dispatch]);

  useEffect(() => {
    // If injuryEntries exists and has data, use it
    if (injuryEntries && injuryEntries.length > 0) {
      setEntries(injuryEntries);
    }
  }, [injuryEntries]);

  // Initialize entries when user selects "yes" and entries is empty
  useEffect(() => {
    if (injuryEntryChange === "yes" && entries.length === 0) {
      setEntries([
        { id: Date.now(), type: "surgery", name: "", date: "", details: "" },
      ]);
    }
  }, [injuryEntryChange, entries.length]);
  useEffect(() => {
    dispatch(handleUpdateInjuryHistory(entries));
    dispatch(
      handleUpdateErrorsOfOrderCreamForm({ isError: false, errors: [] })
    );
  }, [entries]);

  const handleBack = () => {
    if (currentStep > 0) {
      const prevStep = currentStep - 1;
      const newParams = new URLSearchParams(searchParams);
      newParams.set("step", prevStep.toString());
      setSearchParams(newParams, { replace: true });
      // Don't call handleGoBack() - let the parent component sync from URL
    }
  };

  const handleSubmit = async () => {
    // Validate radio buttons first - must be "yes" or "no"
    const validValues = ["yes", "no"];

    if (
      !hasOpenWound ||
      hasOpenWound === "" ||
      hasOpenWound === null ||
      hasOpenWound === undefined ||
      (typeof hasOpenWound === "string" && hasOpenWound.trim() === "") ||
      !validValues.includes(hasOpenWound)
    ) {
      return dispatch(
        handleUpdateErrorsOfOrderCreamForm({
          isError: true,
          errors: ["Please choose if you have an open wound or not."],
        })
      );
    }

    if (
      !injuryEntryChange ||
      injuryEntryChange === "" ||
      injuryEntryChange === null ||
      injuryEntryChange === undefined ||
      (typeof injuryEntryChange === "string" &&
        injuryEntryChange.trim() === "") ||
      !validValues.includes(injuryEntryChange)
    ) {
      return dispatch(
        handleUpdateErrorsOfOrderCreamForm({
          isError: true,
          errors: ["Please choose if you have an injury history or not."],
        })
      );
    }

    const { isError, errors } = validateOrderCreamInjuryHistory({
      hasOpenWound: hasOpenWound,
      injuryHistory: injuryEntryChange,
    });
    if (!isError) {
      if (injuryEntryChange === "yes") {
        if (entries.length < 1) {
          const isError = true;
          const errors = ["Please complete your response"];
          return dispatch(
            handleUpdateErrorsOfOrderCreamForm({ isError, errors })
          );
        }
        for (let i = 0; i < entries.length; i++) {
          const entry = entries[i];
          if (!entry.date || !entry.name || !entry.date || !entry.details) {
            const errors = ["Please complete your response"];
            return dispatch(
              handleUpdateErrorsOfOrderCreamForm({ isError: true, errors })
            );
          }
        }
      }

      if (!transactionId) {
        return dispatch(
          handleUpdateErrorsOfOrderCreamForm({
            isError: true,
            errors: ["Transaction ID is missing. Please refresh the page."],
          })
        );
      }

      try {
        // Sanitize injuryHistory to remove _id fields
        const sanitizedInjuryHistory = {
          hasOpenWound: hasOpenWound,
          injuryEntryChange: injuryEntryChange,
          injuryEntries: entries.map((entry: any) => {
            const { _id, ...rest } = entry;
            return rest;
          }),
        };

        await dispatch(
          saveStep6Api({
            transactionId,
            injuryHistory: sanitizedInjuryHistory,
          })
        ).unwrap();

        // Update URL with new step
        const newParams = new URLSearchParams(searchParams);
        newParams.set("step", "7");
        setSearchParams(newParams, { replace: true });

        dispatch(handleGoToNextStep());
      } catch (error) {
        dispatch(
          handleUpdateErrorsOfOrderCreamForm({
            isError: true,
            errors: ["Failed to save step. Please try again."],
          })
        );
      }
      return;
    }
    return dispatch(handleUpdateErrorsOfOrderCreamForm({ isError, errors }));
  };
  return (
    <div className="injury-history-entry">
      <div className="flex flex-col gap-5 w-full">
        {/* Open Sore/Wound Question */}
        <div className="flex flex-col gap-2">
          <label className="h6 text-theme-text-secondary">
            <strong>
              Do you have an open sore or wound around the area of pain? *
            </strong>
          </label>
          <Radio.Group onChange={handleOpenWoundChange} value={hasOpenWound}>
            <Radio value="yes">Yes</Radio>
            <Radio value="no">No</Radio>
          </Radio.Group>
        </div>

        {/* History of Surgery/Injury/Accident Question */}
        <div className="flex flex-col gap-2">
          <label className="h6 text-theme-text-secondary">
            <strong>
              Do you have a history of surgery, injury, or accident? *
            </strong>
          </label>
          <Radio.Group
            onChange={handlePreviousdiagChange}
            value={injuryEntryChange}
          >
            <Radio value="yes">Yes</Radio>
            <Radio value="no">No</Radio>
          </Radio.Group>
        </div>

        {/* Dynamic Injury Entries */}
        {injuryEntryChange === "yes" &&
          entries.map((entry, index) => (
            <div key={entry.id} className="flex flex-col gap-2">
              <label className="h6 text-theme-text-secondary">
                <strong>{`Entry #${index + 1}`}</strong>
              </label>
              <div className="flex flex-col gap-3">
                <div className="flex gap-3">
                  <div style={{ width: "180px", flexShrink: 0 }}>
                    <Select
                      defaultValue="surgery"
                      value={entry.type}
                      onChange={(value) =>
                        handleEntryChange(entry.id, "type", value)
                      }
                      style={{ width: "100%" }}
                    >
                      <Option value="surgery">Surgery</Option>
                      <Option value="injury">Injury/Accident</Option>
                      <Option value="fall">Fall</Option>
                      <Option value="other">Other</Option>
                    </Select>
                  </div>
                  <div className="flex-1">
                    <Input
                      placeholder="Type of injury/accident"
                      value={entry.name}
                      onChange={(e) =>
                        handleEntryChange(entry.id, "name", e.target.value)
                      }
                      style={{ width: "100%" }}
                    />
                  </div>
                </div>
                <div className="flex gap-3 items-end">
                  <div className="flex-1">
                    <Input
                      placeholder="Injuries Sustained"
                      value={entry.details}
                      onChange={(e) =>
                        handleEntryChange(entry.id, "details", e.target.value)
                      }
                      style={{ width: "100%" }}
                    />
                  </div>
                  <div className="flex-1">
                    <DatePicker
                      value={entry.date ? dayjs(entry.date) : null}
                      placeholder="Date of injury/accident"
                      onChange={(date, dateString) => {
                        if (date) {
                          handleEntryChange(
                            entry.id,
                            "date",
                            date.toISOString()
                          );
                        }
                      }}
                      style={{ width: "100%" }}
                    />
                  </div>
                  <div className="flex gap-2">
                    <Button shape="circle" icon="+" onClick={handleAddEntry} />
                    {index > 0 && (
                      <Button
                        shape="circle"
                        icon="-"
                        onClick={() => handleRemoveEntry(entry.id)}
                      />
                    )}
                  </div>
                </div>
              </div>
            </div>
          ))}
        {/* Error Display */}
        {isError === true && (
          <div className="mt-6">
            <ErrorList errors={errors} />
          </div>
        )}

        {/* Submit Button */}
        <div
          className="flex justify-center gap-4"
          style={{ marginTop: "40px" }}
        >
          <button
            onClick={handleBack}
            className="caption2 transition-colors duration-200"
            style={{
              color: "var(--primary-500)",
              textDecoration: "none",
              background: "none",
              border: "none",
              cursor: "pointer",
            }}
          >
            Previous
          </button>
          <button
            onClick={handleSubmit}
            className="btn btn-primary btn-lg"
            disabled={isLoading}
            style={{ position: "relative", minWidth: "120px" }}
          >
            {isLoading ? (
              <span
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  gap: "8px",
                }}
              >
                <Spin size="small" />
                Saving...
              </span>
            ) : (
              "Next"
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default OcInjuryhistory;
